/**
 * Filtering for member-written text — profile names and bios, event titles and
 * descriptions.
 *
 * This is a first line of defence, not the whole answer. It exists so obvious
 * abuse never reaches another member's screen; everything subtler is caught by
 * people reporting it and an operator acting on the report. Treating a word list
 * as sufficient moderation is the mistake this comment exists to prevent.
 *
 * Deliberately narrow: slurs and explicit sexual language, the categories App
 * Store guideline 1.2 calls objectionable. Ordinary profanity is not blocked —
 * runners swear, and a filter that rejects "bloody hills" trains people to fight
 * the app rather than trusting it.
 */

export type ContentSafetyField = 'bio' | 'description' | 'name' | 'title';

export type ContentSafetyVerdict =
  Readonly<{ isAllowed: true }> | Readonly<{ isAllowed: false; match: string; message: string }>;

/**
 * Confusable characters collapse to the letter they imitate, so `f u c k`,
 * `f.u.c.k` and `fu(k` all reduce to the same thing. Without this the filter is
 * defeated by anyone who has used the internet.
 */
const CONFUSABLES: Readonly<Record<string, string>> = {
  '0': 'o',
  '1': 'i',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '7': 't',
  '8': 'b',
  '@': 'a',
  $: 's',
  '!': 'i',
  '(': 'c',
  '|': 'i',
};

/**
 * Terms that make a surface unusable for other members. Kept short and specific:
 * every entry is a word whose presence in a run club profile is never a false
 * alarm. Substring matching is intentional — padding a slur with letters is the
 * commonest evasion — which is why nothing ambiguous belongs in this list.
 */
const BLOCKED_TERMS: readonly string[] = [
  'nigger',
  'nigga',
  'faggot',
  'tranny',
  'chink',
  'spic',
  'kike',
  'retard',
  'rape',
  'childporn',
  'cp0rn',
  'kiddieporn',
  'bestiality',
];

const FIELD_LABEL: Readonly<Record<ContentSafetyField, string>> = {
  bio: 'bio',
  description: 'description',
  name: 'name',
  title: 'title',
};

/**
 * Reduces text to a comparable core: lower case, accents stripped, confusables
 * folded, and everything that is not a letter removed. Separators disappear
 * entirely, so spacing and punctuation cannot be used to smuggle a term through.
 */
export function normalizeForContentSafety(value: string): string {
  const folded = value
    .toLowerCase()
    .normalize('NFKD')
    // Combining accents, once NFKD has split them off the base letter.
    .replaceAll(/[̀-ͯ]/g, '');

  let result = '';
  for (const character of folded) {
    const mapped = CONFUSABLES[character] ?? character;
    if (mapped >= 'a' && mapped <= 'z') {
      result += mapped;
    }
  }
  return result;
}

/**
 * Whether a piece of member-written text may be published.
 *
 * Returns the term that matched so the caller can log what happened, and a
 * message written for the person typing: it says the text was rejected and why,
 * without repeating the slur back at them or hinting at how to slip past.
 */
export function checkContentSafety(value: string, field: ContentSafetyField): ContentSafetyVerdict {
  const normalized = normalizeForContentSafety(value);
  if (!normalized) {
    return { isAllowed: true };
  }

  const match = BLOCKED_TERMS.find((term) => normalized.includes(term));
  if (!match) {
    return { isAllowed: true };
  }

  return {
    isAllowed: false,
    match,
    message: `This ${FIELD_LABEL[field]} contains language we do not allow. 6am has no tolerance for abusive or objectionable content, so please rewrite it.`,
  };
}

/** First failing field, or null when every field is publishable. */
export function firstContentSafetyFailure(
  fields: readonly Readonly<{ field: ContentSafetyField; value: string | null | undefined }>[]
): (ContentSafetyVerdict & { isAllowed: false }) | null {
  for (const { field, value } of fields) {
    if (!value) {
      continue;
    }
    const verdict = checkContentSafety(value, field);
    if (!verdict.isAllowed) {
      return verdict;
    }
  }
  return null;
}
